import { useEffect, useState } from 'react';
import { Modal, Button, Textarea } from '../../../shared/components';
import { OperationTimeRecord } from '../../../shared/services/operationTimes/types';

interface DeleteRecordModalProps {
  record: OperationTimeRecord | null;
  onClose: () => void;
  onConfirm: (id: string, reason: string) => Promise<void>;
}

/**
 * Confirmacion de eliminacion de un registro de Tiempos - requiere justificacion
 * obligatoria (columna "Modificado / Borrado" del historial). Si se intenta
 * confirmar sin diligenciarla, el campo se resalta en amarillo y aparece una alerta.
 */
export function DeleteRecordModal({ record, onClose, onConfirm }: DeleteRecordModalProps) {
  const [reason, setReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setReason('');
    setReasonMissing(false);
  }, [record]);

  if (!record) return null;

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setReasonMissing(true);
      return;
    }
    setIsDeleting(true);
    try {
      await onConfirm(record.id, reason.trim());
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal open={!!record} onClose={onClose} title="Eliminar registro">
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          ¿Seguro que deseas eliminar el registro <span className="font-semibold">{record.code}</span>? Quedará visible en el historial marcado como
          "Borrado" junto con el motivo. Esta acción no se puede deshacer.
        </p>
        <div>
          <Textarea
            label="Motivo de la eliminación *"
            placeholder="Ej: Se duplicó el registro por error al guardar dos veces."
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonMissing(false);
            }}
            className={reasonMissing ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300' : ''}
          />
          {reasonMissing && <p className="mt-1 text-xs font-medium text-amber-700">⚠️ Debes justificar por qué se elimina este registro para poder continuar.</p>}
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" fullWidth onClick={onClose}>Cancelar</Button>
          <Button variant="danger" fullWidth onClick={handleConfirm} isLoading={isDeleting}>Eliminar</Button>
        </div>
      </div>
    </Modal>
  );
}
